import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { compose } from 'react-recompose';
import { withStyles } from '@material-ui/core/styles';
import styled from 'styled-components';

import { Grid, Typography as Text, Button, Chip } from '@material-ui/core';
import Skeleton from '@material-ui/lab/Skeleton';

import {
    AttachMoney as PaymentIcon,
    Person as ProfileIcon,
    SettingsApplications as SettingsIcon,
    LocalPlay as ReputationIcon,
    Security as SecurityIcon,
    MenuBook as LegalIcon,
} from '@material-ui/icons';

import { palette, Container } from '@dev/ee-components';

import { ApplicationState as State } from '@api/store';
import { MyAccountProps } from './MyAccountInterface';

const styles = {
    root: {
        padding: '2.4em 4.8em',
        'justify-content': 'left',
        borderBottom: `1px solid #ddd`,
        '&:hover': {
            background: palette.secondary.main,
        },
        '&:focus': {
            background: palette.secondary.main,
        },
        '&:active': {
            background: palette.secondary.main,
        },
    },
    text: {
        'text-align': 'left',
    },
    startIcon: {
        'margin-right': '3em',
        color: palette.indigo[3],
    },
    label: {
        'text-transform': 'none',
    },
};

const Header = styled.div`
    display: flex;
    align-items: center;
    & :last-child {
        margin-left: auto;
    }
`;

const StatusContainer = styled.div``;

const BtnContent = styled.div`
    display: flex;
    flex-flow: column;
`;

const Content = styled.div`
    background: #fff;
    margin-top: 1em;
`;

const mapState = (state: State, ownProps: MyAccountProps): MyAccountProps => {
    return {
        ...ownProps,
        account: state.account,
        browser: state.browser,
    };
};

class MyAccount extends Component<MyAccountProps> {
    private sections = [
        {
            icon: <PaymentIcon />,
            title: 'payments',
            description: 'billingdesc',
        },
        {
            icon: <ProfileIcon />,
            title: 'profile',
            description: 'profiledesc',
        },
        {
            icon: <SettingsIcon />,
            title: 'settings',
            description: 'settingsdesc',
        },
        {
            icon: <ReputationIcon />,
            title: 'reputation',
            description: 'reputationdesc',
        },
        {
            icon: <SecurityIcon />,
            title: 'security',
            description: 'securitydesc',
        },
        {
            icon: <LegalIcon />,
            title: 'legal',
            description: 'legaldesc',
            url: '/my-account/legal',
        },
    ];

    private getStatusBadge(): JSX.Element {
        const { account } = this.props;

        if (account.accountLoading) {
            return <Skeleton width={100} height={40} />;
        }

        switch (account.GeneralInfo.StatusFormatted) {
            case 'Active':
                return (
                    <Chip
                        style={{ backgroundColor: palette.green[5], color: '#fff', marginLeft: '.5em' }}
                        size="small"
                        label={account.GeneralInfo.StatusFormatted}
                    />
                );
            case 'NeverSignedIn':
            case 'NoPaymentsAllowed':
            case 'UnderReview':
                return (
                    <Chip
                        style={{ backgroundColor: palette.yellow[5], color: '#fff', marginLeft: '.5em' }}
                        size="small"
                        label={account.GeneralInfo.StatusFormatted}
                    />
                );
            case 'Disabled':
            case 'Deleted':
            case 'Purged':
                return <Chip label={account.GeneralInfo.StatusFormatted} />;
            default:
                return (
                    <Chip
                        style={{ backgroundColor: palette.red[5], color: '#fff', marginLeft: '.5em' }}
                        size="small"
                        label={account.GeneralInfo.StatusFormatted}
                    />
                );
        }
    }

    public render(): JSX.Element {
        const { classes, account, t, browser } = this.props;

        return (
            <Container variant="full">
                <Grid container spacing={0} direction="column">
                    <Header>
                        <Text variant="h1" align="left">
                            {t('myaccount')}
                        </Text>
                        <StatusContainer>
                            <span>{t('status')}:</span>
                            {this.getStatusBadge()}
                        </StatusContainer>
                    </Header>
                    <Content>
                        {this.sections.map((elem, index) => (
                            <Button
                                key={`buttonMyAccount-${index}`}
                                classes={classes}
                                fullWidth={true}
                                startIcon={elem.icon}
                                size={'large'}
                                onClick={(): void => {
                                    if (!elem.url) {
                                        return;
                                    }

                                    this.props.history.push(elem.url);
                                }}
                            >
                                <BtnContent>
                                    {account.accountLoading && <Skeleton width={300} height={40} />}
                                    {!account.accountLoading && (
                                        <Text variant="h3" align="left" gutterBottom>
                                            {t(elem.title)}
                                        </Text>
                                    )}
                                    {account.accountLoading && browser.greaterThan.sm && <Skeleton width={400} height={20} />}
                                    {!account.accountLoading && browser.greaterThan.sm && (
                                        <Text variant="caption" align="left">
                                            {t(elem.description)}
                                        </Text>
                                    )}
                                </BtnContent>
                            </Button>
                        ))}
                    </Content>
                </Grid>
            </Container>
        );
    }
}

export default compose(withTranslation(), withStyles(styles), connect<MyAccountProps>(mapState))(MyAccount);
